import { useCallback, useMemo, type CSSProperties } from 'react';

import type {
  FieldSuggestion,
  ProcessType,
  SupplierMaterial,
} from '@frontend/api';
import { Select } from '@frontend/components';

import type { RootState } from 'store/configureStore';
import { useAppSelector } from 'store/hooks';
import { getMaterialOptionsForCascaderForSupplierMaterials } from 'store/materials/reducer';

import MaterialSelector from './MaterialSelector';
import type {
  MaterialCascaderNode,
  MaterialSelectorChangeHandler,
} from './types';

type ProcessMaterialSelectorProps = {
  canSelectSubmenu?: boolean;
  disabled: boolean;
  materialFieldSuggestions?: FieldSuggestion[];
  menuPortalDestinationRef: React.RefObject<HTMLDivElement>;
  onChange: MaterialSelectorChangeHandler;
  placeholder: string;
  process?: ProcessType;
  selectedMaterialId: number | null | string;
  selectedSupplierMaterialId: number | string | null;
};

type ProcessMaterialSelectorOption = {
  supplierMaterialId: number;
  materialId: number;
  label: string;
};

const ProcessMaterialSelector = ({
  canSelectSubmenu = false,
  disabled,
  materialFieldSuggestions,
  menuPortalDestinationRef,
  onChange, // passed as direct prop
  placeholder = 'Select material',
  process,
  selectedMaterialId,
  selectedSupplierMaterialId,
}: ProcessMaterialSelectorProps) => {
  const isCascaderMode = Boolean(
    process && !process.useListDisplayForProcessMaterials,
  );

  const supplierMaterialsForCascader = useMemo(() => {
    if (
      process?.processMaterials &&
      !process.useListDisplayForProcessMaterials
    ) {
      return process.processMaterials.map((pm) => ({
        ...pm.supplierMaterial,
        materialId: pm.supplierMaterial.material.id,
      }));
    }
    return null;
  }, [process]);

  const cascaderOptions: MaterialCascaderNode[] = useAppSelector(
    (state: RootState) =>
      supplierMaterialsForCascader
        ? getMaterialOptionsForCascaderForSupplierMaterials(
            state,
            supplierMaterialsForCascader,
          )
        : [],
  );

  const processMaterialOptions = useMemo(() => {
    return isCascaderMode || !process?.processMaterials
      ? []
      : process?.processMaterials
          .map((pm) => pm.supplierMaterial)
          .sort((a, b) =>
            a.displayName.localeCompare(b.displayName.toLocaleLowerCase()),
          )
          .map((supplierMaterial: SupplierMaterial) => ({
            materialId: supplierMaterial.material.id,
            supplierMaterialId: supplierMaterial.id,
            label: supplierMaterial.displayName,
          }));
  }, [isCascaderMode, process]);

  const selectedListValue = useMemo(
    () =>
      processMaterialOptions.find(
        (processMaterial) =>
          processMaterial.supplierMaterialId === selectedSupplierMaterialId &&
          processMaterial.materialId === selectedMaterialId,
      ) ?? null,
    [processMaterialOptions, selectedMaterialId, selectedSupplierMaterialId],
  );

  const handleListSelectChange = useCallback(
    (option: ProcessMaterialSelectorOption | null) => {
      onChange({
        supplierMaterialId: option?.supplierMaterialId ?? null,
        materialId: option?.materialId ?? null,
      });
    },
    [onChange],
  );

  const selectStyles = useMemo(
    () => ({
      // NB: Preserved comment from 3 years ago: "fairly certain this doesn't affect the style"
      menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 9999 }),
    }),
    [],
  );

  return isCascaderMode ? (
    <MaterialSelector
      cascaderOptions={cascaderOptions}
      canSelectSubmenu={canSelectSubmenu}
      disabled={disabled}
      materialFieldSuggestions={materialFieldSuggestions}
      materialId={selectedMaterialId}
      onChange={onChange}
      placeholder={placeholder}
      supplierMaterialId={selectedSupplierMaterialId}
    />
  ) : (
    <Select
      onChange={handleListSelectChange}
      options={processMaterialOptions}
      value={selectedListValue}
      styles={selectStyles}
      menuPortalDestinationRef={menuPortalDestinationRef}
      menuPortalTarget={document.body}
      isDisabled={disabled}
      placeholder={placeholder}
      aria-label="Select material"
    />
  );
};

export default ProcessMaterialSelector;
